import * as React from "react";
import { useEffect, useState } from "react";
import PropTypes from "prop-types";

import { useTheme } from "@mui/material/styles";

import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Table,
  TableBody,
  TablePagination,
  Paper,
  TextField,
  InputAdornment,
  Switch,
  Tooltip,
  IconButton,
} from "@mui/material";

import TableSortLabel from "@mui/material/TableSortLabel";

import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import FilterAltIcon from "@mui/icons-material/FilterAlt";

import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";

import FeatherIcon from "feather-icons-react";

import { toast } from "react-toastify";

import { useFormik } from "formik";

import * as Yup from "yup";

import {
  GetDefaultPagesAccessList,
  AddDefaultPagesAccess,
  UpdateDefaultPagesAccess,
  GetDashboardModulePagesList,
  GetDashboardModulesList,
} from "../../../services/admin";

import CustomFormLabel from "../../../components/forms/custom-elements/CustomFormLabel";

import CustomTextField from "../../../components/forms/custom-elements/CustomTextField";

function TablePaginationActions(props) {
  const theme = useTheme();

  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box
      sx={{
        flexShrink: 0,
        ml: 2.5,
      }}
    >
      <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0}>
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>

      <IconButton onClick={handleBackButtonClick} disabled={page === 0}>
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>

      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>

      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,

  onPageChange: PropTypes.func.isRequired,

  page: PropTypes.number.isRequired,

  rowsPerPage: PropTypes.number.isRequired,
};

const AddDefaultPageAccess = () => {
  const [list, setList] = useState([]);

  const [modulePages, setModulePages] = useState([]);

  const [modules, setModules] = useState([]);

  const [filteredPages, setFilteredPages] = useState([]);

  const [editId, setEditId] = useState(null);

  const [page, setPage] = React.useState(0);

  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  const [searchText, setSearchText] = useState({});

  const [globalSearch, setGlobalSearch] = useState("");

  const [showAllFilters, setShowAllFilters] = useState(false);

  const [activeFilter, setActiveFilter] = useState("");

  const [orderDirection, setOrderDirection] = useState("asc");

  const [valueToOrderBy, setValueToOrderBy] = useState("defaultPageAccessID");
  
  const formik = useFormik({
    initialValues: {
      moduleID: "",
      modulePageID: "",
      isActive: true,
    },

validationSchema: Yup.object({
  moduleID: Yup.number().required("Module is required"),

  modulePageID: Yup.number().required("Page is required"),
}),
    onSubmit: async (values, { resetForm }) => {
  try {
    const payload = {
      defaultPageAccessID: editId || 0,
      modulePageID: values.modulePageID,
      isActive: values.isActive,
    };

    if (editId) {
      await UpdateDefaultPagesAccess(payload);

      toast.success(
        "Default Page Access updated successfully"
      );
    } else {
      const response =
        await AddDefaultPagesAccess(payload);

      if (
        response?.data?.[0]?.message ===
        "ALREADY_EXISTS"
      ) {
        toast.warning(
          "Page already configured as default."
        );
        return;
      }

      toast.success(
        "Default Page Access added successfully"
      );
    }

    getDefaultPagesAccess();

    resetForm();

    setEditId(null);
  } catch (error) {
    console.log(error);

    toast.error(
      error?.response?.data?.message ||
      error?.response?.data ||
      error?.message ||
      "Save failed"
    );
  }
},
  });

  

  const getDefaultPagesAccess = async () => {
    try {
      const response = await GetDefaultPagesAccessList();

      if (response?.status === 200) {
        setList(response?.data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getModules = async () => {
  try {
    const response =
      await GetDashboardModulesList();

    if (response?.status === 200) {
      setModules(response.data);
    }
  } catch (error) {
    console.log(error);
  }
};

  const getModulePages = async () => {
  try {
    const response =
      await GetDashboardModulePagesList();

    if (response?.status === 200) {
      setModulePages(response.data);
    }
  } catch (error) {
    console.log(error);
  }
};

  useEffect(() => {
    getDefaultPagesAccess();
    getModulePages();
    getModules();
  }, []);

  useEffect(() => {
  if (!formik.values.moduleID) {
    setFilteredPages([]);
    return;
  }

  const pages = modulePages.filter(
    (x) =>
      Number(x.moduleID) ===
      Number(formik.values.moduleID)
  );
  setFilteredPages(pages);
}, [formik.values.moduleID, modulePages]);

  const handleEdit = (row) => {
    setEditId(row.defaultPageAccessID);

   formik.setValues({
  moduleID: row.moduleID,
  modulePageID: row.modulePageID,
  isActive: row.isActive,
});
    window.scrollTo({
      top: 0,
      behavior: "smooth",

    });

  };

  const handleToggleStatus = async (row, checked) => {
    try {
      const payload = {
        defaultPageAccessID: row.defaultPageAccessID,
        modulePageID: row.modulePageID,
        isActive: checked,
      };

      await UpdateDefaultPagesAccess(payload);
      toast.success(`Module ${checked ? "Activated" : "Deactivated"}`);

      getDefaultPagesAccess();
    } catch (error) {
      toast.error("Status update failed");
    }
  };

  const handleResetFilters = () => {
    setGlobalSearch("");

    setSearchText({});

    setShowAllFilters(false);

    setActiveFilter("");

    setPage(0);
  };

  const handlRequestSort = (property) => {
    const isAscending = valueToOrderBy === property && orderDirection === "asc";

    setValueToOrderBy(property);

    setOrderDirection(isAscending ? "desc" : "asc");
  };

  const filteredData = list.filter((item) => {
    // GLOBAL SEARCH

    const globalMatch = Object.values(item)
      .join(" ")
      .toLowerCase()
      .includes(globalSearch.toLowerCase());

    // ACTIVE FILTER

    const activeMatch =
      activeFilter === "" ? true : item.isActive?.toString() === activeFilter;

    // COLUMN FILTERS

    const columnMatch = Object.keys(searchText).every((key) => {
      if (!searchText[key]) return true;

      const value = item[key]?.toString().toLowerCase() || "";

      return value.includes(searchText[key].toLowerCase());
    });

    return globalMatch && activeMatch && columnMatch;
  });
  return (
    <div>
      {/* FORM */}

      <Card sx={{ p: 0, mb: 1 }}>
        <Box
          display="flex"
          alignItems="center"
          px={2}
          py={1.5}
          sx={{
            backgroundColor: "primary.light",
            color: "primary.main",
          }}
        >
          <FeatherIcon icon="grid" width="18" />

          <Typography variant="subtitle1" sx={{ ml: 1 }}>
            {editId ? "Edit Default Page Access" : "Add Default Page Access"}
          </Typography>
        </Box>

        <CardContent>
  <Grid container spacing={2}>
    {/* Module */}

    <Grid
      item
      lg={3}
      md={6}
      sm={12}
    >
      <CustomFormLabel>
        Module
      </CustomFormLabel>

      <CustomTextField
        select
        fullWidth
        size="small"
        name="moduleID"
        value={formik.values.moduleID}
        onChange={(e) => {
          formik.handleChange(e);

          formik.setFieldValue(
            "modulePageID",
            ""
          );
        }}
        error={
          formik.touched.moduleID &&
          Boolean(
            formik.errors.moduleID
          )
        }
        helperText={
          formik.touched.moduleID &&
          formik.errors.moduleID
        }
        SelectProps={{
          native: true,
        }}
      >
        <option value="">
          Select Module
        </option>

        {modules.map((item) => (
          <option
            key={item.moduleID}
            value={item.moduleID}
          >
            {item.moduleName}
          </option>
        ))}
      </CustomTextField>
    </Grid>

    {/* Page */}

    <Grid
      item
      lg={3}
      md={6}
      sm={12}
    >
      <CustomFormLabel>
        Page
      </CustomFormLabel>

      <CustomTextField
        select
        fullWidth
        size="small"
        name="modulePageID"
        value={formik.values.modulePageID}
        onChange={formik.handleChange}
        error={
          formik.touched.modulePageID &&
          Boolean(
            formik.errors.modulePageID
          )
        }
        helperText={
          formik.touched.modulePageID &&
          formik.errors.modulePageID
        }
        SelectProps={{
          native: true,
        }}
      >
        <option value="">
          Select Page
        </option>

        {filteredPages.map((item) => (
          <option
            key={item.modulePageID}
            value={item.modulePageID}
          >
            {item.pageName}
          </option>
        ))}
      </CustomTextField>
    </Grid>

    {/* Status */}

    <Grid
      item
      lg={2}
      md={6}
      sm={12}
    >
      <CustomFormLabel>
        Status
      </CustomFormLabel>

      <Switch
        checked={formik.values.isActive}
        onChange={(e) =>
          formik.setFieldValue(
            "isActive",
            e.target.checked
          )
        }
      />
    </Grid>

    {/* Buttons */}

    <Grid
      item
      lg={4}
      md={12}
      sm={12}
      display="flex"
      alignItems="center"
      mt={3}
    >
      <Button
        variant="contained"
        onClick={formik.handleSubmit}
      >
        {editId
          ? "Update"
          : "Add"}
      </Button>

      <Button
        sx={{ ml: 2 }}
        variant="outlined"
        onClick={() => {
          formik.resetForm();

          setEditId(null);
        }}
      >
        Reset
      </Button>
    </Grid>
  </Grid>
</CardContent>
      </Card>

      {/* TABLE */}

      <Card sx={{ p: 0 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          py={1.5}
          px={2}
          sx={{
            backgroundColor: "primary.light",
            color: "primary.main",
          }}
        >
          <Box display="flex" alignItems="center">
            <FeatherIcon icon="database" width="18" />

            <Typography variant="subtitle1" sx={{ ml: 1 }}>
              Existing Default Page Access ({filteredData.length})
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1.5}>
            {/* SEARCH */}

            <TextField
              size="small"
              placeholder="Search..."
              value={globalSearch}
              onChange={(e) => {
                setGlobalSearch(e.target.value);

                setPage(0);
              }}
              sx={{
                width: 220,
                backgroundColor: "white",
                borderRadius: 1,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),

                endAdornment: globalSearch && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setGlobalSearch("")}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* ACTIVE FILTER */}

            <TextField
              select
              size="small"
              value={activeFilter}
              onChange={(e) => {
                setActiveFilter(e.target.value);

                setPage(0);
              }}
              sx={{
                minWidth: 140,
                backgroundColor: "white",
                borderRadius: 1,
              }}
              SelectProps={{
                native: true,
              }}
            >
              <option value="">All</option>

              <option value="true">Active</option>

              <option value="false">Inactive</option>
            </TextField>

            {/* FILTER ICON */}

            <FilterAltIcon
              sx={{
                cursor: "pointer",
                color: showAllFilters ? "primary.main" : "inherit",
              }}
              onClick={() => setShowAllFilters(!showAllFilters)}
            />

            {/* RESET */}

            <Button
              variant="outlined"
              size="small"
              onClick={handleResetFilters}
            >
              Reset Filter
            </Button>
          </Box>
        </Box>

        <CardContent>
          <TableContainer
            component={Paper}
            sx={{
              maxHeight: showAllFilters ? 540 : 450,
              overflowY: filteredData.length > 5 ? "auto" : "visible",
              overflowX: "auto",
              scrollbarGutter: "stable",
            }}
          >
            <Table stickyHeader>
              <TableHead
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                }}
              >
<TableRow>
  <TableCell>
    <TableSortLabel
      active={valueToOrderBy === "defaultPageAccessID"}
      direction={
        valueToOrderBy === "defaultPageAccessID"
          ? orderDirection
          : "asc"
      }
      onClick={() =>
        handlRequestSort("defaultPageAccessID")
      }
    >
      ID
    </TableSortLabel>
  </TableCell>

  <TableCell>
    <TableSortLabel
      active={valueToOrderBy === "moduleName"}
      direction={
        valueToOrderBy === "moduleName"
          ? orderDirection
          : "asc"
      }
      onClick={() =>
        handlRequestSort("moduleName")
      }
    >
      Module
    </TableSortLabel>
  </TableCell>

  <TableCell>
    <TableSortLabel
      active={valueToOrderBy === "pageName"}
      direction={
        valueToOrderBy === "pageName"
          ? orderDirection
          : "asc"
      }
      onClick={() =>
        handlRequestSort("pageName")
      }
    >
      Page
    </TableSortLabel>
  </TableCell>

  <TableCell>
    <TableSortLabel
      active={valueToOrderBy === "uniqueCode"}
      direction={
        valueToOrderBy === "uniqueCode"
          ? orderDirection
          : "asc"
      }
      onClick={() =>
        handlRequestSort("uniqueCode")
      }
    >
      Unique Code
    </TableSortLabel>
  </TableCell>

  <TableCell>
    Status
  </TableCell>

  <TableCell align="center">
    Action
  </TableCell>
</TableRow>

                {showAllFilters && (
                  <TableRow>
                    {[
                      "defaultPageAccessID",
                      "moduleName",
                      "pageName",
                      "uniqueCode",
                    ].map((field) => (
                      <TableCell key={field}>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="Search..."
                          value={searchText[field] || ""}
                          onChange={(e) => {
                            setSearchText((prev) => ({
                              ...prev,
                              [field]: e.target.value,
                            }));

                            setPage(0);
                          }}
                          InputProps={{
                            endAdornment: searchText[field] && (
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setSearchText((prev) => ({
                                      ...prev,
                                      [field]: "",
                                    }))
                                  }
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </TableCell>
                    ))}

                    <TableCell />

                    <TableCell />
                  </TableRow>
                )}
              </TableHead>

              <TableBody>
                {filteredData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <>
                        <div>No data found</div>

                        <Button
                          size="small"
                          sx={{ mt: 1 }}
                          onClick={handleResetFilters}
                        >
                          Clear Filters
                        </Button>
                      </>
                    </TableCell>
                  </TableRow>
                )}

                {(rowsPerPage > 0
                  ? filteredData.slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage,
                    )
                  : filteredData
                ).map((row) => (
                  <TableRow key={row.defaultPageAccessID}>
                    <TableCell>{row.defaultPageAccessID}</TableCell>

                    <TableCell>{row.moduleName}</TableCell>

                    <TableCell>{row.pageName}</TableCell>

                    <TableCell>
  {row.uniqueCode}
</TableCell>

                    <TableCell>
                      <Tooltip title={row.isActive ? "Active" : "Inactive"}>
                        <Switch
                          checked={row.isActive}
                          size="small"
                          onChange={(e) =>
                            handleToggleStatus(row, e.target.checked)
                          }
                        />
                      </Tooltip>
                    </TableCell>

                    <TableCell align="center">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(row)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <TablePagination
              rowsPerPageOptions={[
                5,
                10,
                25,
                {
                  label: "All",
                  value: -1,
                },
              ]}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));

                setPage(0);
              }}
              ActionsComponent={TablePaginationActions}
            />
          </Box>
        </CardContent>
      </Card>
    </div>
  );
};

export default AddDefaultPageAccess;
